import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Star, Gift, Mail, MessageCircle, Send, Copy, CheckCircle } from "lucide-react";

export default function MarketingReviewsReferrals() {
  const [stats, setStats] = useState(null);
  const [refs, setRefs] = useState([]);
  const [reviewOrderId, setReviewOrderId] = useState("");
  const [reviewMsg, setReviewMsg] = useState("");
  const [refUserId, setRefUserId] = useState("");
  const [refNote, setRefNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, r] = await Promise.all([
      api.get("/admin/marketing/campaigns/stats"),
      api.get("/admin/marketing/referrals"),
    ]);
    setStats(s.data); setRefs(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const sendReview = async () => {
    if (!reviewOrderId) return toast.error("Order ID required");
    setBusy(true);
    try {
      await api.post("/admin/marketing/review-request", { order_id: reviewOrderId, custom_message: reviewMsg || null });
      toast.success("Review request sent");
      setReviewOrderId(""); setReviewMsg("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const createReferral = async () => {
    if (!refUserId) return toast.error("Customer user_id required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/referral-request", { customer_user_id: refUserId, referrer_note: refNote || null });
      toast.success(`Code created: ${data.referral_code}`);
      setRefUserId(""); setRefNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied!");
  };

  return (
    <div className="space-y-6" data-testid="admin-marketing">
      <h2 className="text-2xl font-bold text-[#1a3c5e] flex items-center gap-2">
        <Send className="w-6 h-6" /> Marketing & Reviews
      </h2>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="campaign-stats">
          <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />Emails Sent</div><div className="text-xl font-bold text-emerald-600">{stats.email_sent}</div></div>
          <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />Failed</div><div className="text-xl font-bold text-rose-600">{stats.email_failed}</div></div>
          <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-xs text-slate-500 flex items-center gap-1"><MessageCircle className="w-3 h-3" />WA Sent</div><div className="text-xl font-bold text-emerald-600">{stats.wa_sent}</div></div>
          <div className="p-4 rounded-xl bg-white border border-slate-200"><div className="text-xs text-slate-500">Follow-Ups Sent</div><div className="text-xl font-bold text-blue-600">{stats.follow_up_sent}</div></div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Review request */}
        <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="review-card">
          <h3 className="font-semibold text-[#1a3c5e] flex items-center gap-2 mb-3"><Star className="w-5 h-5" /> Send Review Request</h3>
          <p className="text-sm text-slate-600 mb-3">Request a Google review from a customer after project completion.</p>
          <input
            value={reviewOrderId}
            onChange={(e) => setReviewOrderId(e.target.value)}
            placeholder="Order ID"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-2"
            data-testid="review-order-input"
          />
          <textarea
            value={reviewMsg}
            onChange={(e) => setReviewMsg(e.target.value)}
            placeholder="Optional custom message"
            rows={3}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-3"
            data-testid="review-message-input"
          />
          <button onClick={sendReview} disabled={busy} className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="send-review-btn">
            {busy ? "Sending..." : "Send Review Request"}
          </button>
        </div>

        {/* Referral */}
        <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="referral-card">
          <h3 className="font-semibold text-[#1a3c5e] flex items-center gap-2 mb-3"><Gift className="w-5 h-5" /> Create Referral Code</h3>
          <p className="text-sm text-slate-600 mb-3">Generate a referral code for an existing customer.</p>
          <input
            value={refUserId}
            onChange={(e) => setRefUserId(e.target.value)}
            placeholder="Customer user_id"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-2"
            data-testid="referral-user-input"
          />
          <input
            value={refNote}
            onChange={(e) => setRefNote(e.target.value)}
            placeholder="Note (optional)"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-3"
            data-testid="referral-note-input"
          />
          <button onClick={createReferral} disabled={busy} className="w-full py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold" data-testid="create-referral-btn">
            {busy ? "Creating..." : "Create Referral"}
          </button>
        </div>
      </div>

      <div className="p-5 bg-white rounded-2xl border border-slate-200" data-testid="referrals-list">
        <h3 className="font-semibold text-[#1a3c5e] mb-3">All Referrals</h3>
        {refs.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">No referrals yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b border-slate-100">
              <th className="pb-2">Code</th><th>Customer</th><th>Uses</th><th>Created</th><th></th>
            </tr></thead>
            <tbody>
              {refs.map((r) => (
                <tr key={r.id} className="border-b border-slate-50">
                  <td className="py-2 font-mono font-semibold text-[#1a3c5e]">{r.referral_code}</td>
                  <td className="text-xs text-slate-500">{r.customer_user_id?.slice(0, 12)}...</td>
                  <td><span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold">{r.uses}</span></td>
                  <td className="text-xs text-slate-500">{r.created_at?.slice(0, 10)}</td>
                  <td><button onClick={() => copy(r.referral_code)} className="p-1 hover:bg-slate-100 rounded"><Copy className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
